type RegisterAddress = {
  addressLine?: string;
  apartmentNumber?: string;
  postalCode: string;
  city?: string;
  state?: string;
  country?: string;
  county?: string;
  addressType: string;
};

type RegisterContact = {
  firstName?: string;
  lastName?: string;
  email?: string;
  dob?: string | null;
  is_primary?: boolean;
  gender?: string;
  race?: string;
  ethinicity?: string;
  militaryStatus?: string;
  isMigrantFarmWorker?: boolean | null;
  isDisabled?: boolean | null;
  citizenship?: string;
  isReceivingIncome?: boolean | null;
  ssn?: string;
};

export type SeniorCenterRegisterRequest = {
  name: string;
  email: string;
  phone: string;
  isConfirmed: boolean;
  identificationType: string;
  identificationNumber: string;
  addresses: RegisterAddress[];
  contacts: RegisterContact[];
};

const firstNonEmpty = (...values: Array<string | undefined | null>): string => {
  for (const value of values) {
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
};

const parseNullableBool = (value: unknown): boolean | null => {
  if (value == null || value === '') {
    return null;
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'yes' || normalized === 'true') {
    return true;
  }
  if (normalized === 'no' || normalized === 'false') {
    return false;
  }
  return null;
};

const parseDob = (dob: string): string | null => {
  if (!dob?.trim()) {
    return null;
  }
  const parsed = new Date(dob);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString();
};

export const mapSeniorCenterApplyPayloadToRegisterRequest = (
  payload: Record<string, unknown>
): SeniorCenterRegisterRequest => {
  const contactInfo = (payload['contact_information'] as Record<string, unknown>) ?? {};
  const recipients = Array.isArray(payload['recipients']) ? payload['recipients'] : [];
  const recipient = (recipients[0] as Record<string, unknown>) ?? {};

  const email = firstNonEmpty(
    contactInfo['email_address'] as string,
    contactInfo['primary_contact_email'] as string,
    payload['primary_contact_email'] as string,
    recipient['recipient_email'] as string,
    recipient['email'] as string
  );

  // Prefer recipient phone — that is the participant number shown at card check-in.
  const phone = firstNonEmpty(
    recipient['phone'] as string,
    recipient['recipient_phone'] as string,
    contactInfo['phone'] as string
  );

  // Prefer recipient identity — senior center check-in verifies the participant, not the household primary.
  const firstName = firstNonEmpty(
    recipient['first_name'] as string,
    recipient['recipient_first_name'] as string,
    payload['first_name'] as string
  );
  const lastName = firstNonEmpty(
    recipient['last_name'] as string,
    recipient['recipient_last_name'] as string,
    payload['last_name'] as string
  );
  const dob = firstNonEmpty(
    recipient['dob'] as string,
    recipient['date_of_birth'] as string,
    recipient['recipient_dob'] as string,
    recipient['recipient_date_of_birth'] as string,
    payload['dob'] as string
  );
  const householdName = firstNonEmpty(
    payload['household_name'] as string,
    `${firstName} ${lastName}`.trim(),
    recipient['recipient_name'] as string,
    recipient['name'] as string
  );
  const ssn = String(recipient['ssn'] ?? '');

  const addresses: RegisterAddress[] = [];
  const addressArray = payload['address'];
  if (Array.isArray(addressArray)) {
    for (const item of addressArray) {
      const row = item as Record<string, unknown>;
      addresses.push({
        addressLine: String(row['address_line'] ?? ''),
        apartmentNumber: String(row['apartment_number'] ?? ''),
        postalCode: firstNonEmpty(row['postal_code'] as string, '00000'),
        city: String(row['city'] ?? ''),
        state: String(row['state'] ?? ''),
        country: firstNonEmpty(row['country'] as string, 'United States'),
        county: String(row['county'] ?? ''),
        addressType: firstNonEmpty(row['address_type'] as string, 'residential'),
      });
    }
  }

  if (addresses.length === 0) {
    addresses.push({
      addressLine: 'Pending',
      apartmentNumber: '',
      postalCode: '00000',
      city: '',
      state: '',
      country: 'United States',
      county: '',
      addressType: 'residential',
    });
  }

  return {
    name: householdName,
    email,
    phone,
    isConfirmed: true,
    identificationType: 'Social Security Number (SSN)',
    identificationNumber: ssn,
    addresses,
    contacts: [
      {
        firstName,
        lastName,
        email,
        dob: parseDob(dob),
        is_primary: true,
        gender: firstNonEmpty(
          recipient['sex'] as string,
          recipient['gender'] as string,
          payload['gender'] as string
        ),
        race: firstNonEmpty(recipient['race'] as string, payload['race'] as string),
        ethinicity: firstNonEmpty(
          recipient['ethnicity'] as string,
          payload['ethnicity'] as string
        ),
        militaryStatus: String(payload['military_status'] ?? ''),
        isMigrantFarmWorker: parseNullableBool(payload['is_migrant_farm_worker']),
        isDisabled: parseNullableBool(
          firstNonEmpty(recipient['is_disabled'] as string, payload['is_disabled'] as string)
        ),
        citizenship: firstNonEmpty(
          recipient['citizenship'] as string,
          payload['citizenship'] as string
        ),
        isReceivingIncome: parseNullableBool(
          firstNonEmpty(
            recipient['is_receiving_income'] as string,
            payload['is_receiving_income'] as string
          )
        ),
        ssn,
      },
    ],
  };
};

const isEmptyAddress = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') {
    return true;
  }
  return Object.values(value as Record<string, unknown>).every(
    (field) => field == null || String(field).trim() === ''
  );
};

export const sanitizeSeniorCenterApplyPayload = (
  payload: Record<string, unknown>
): Record<string, unknown> => {
  const sanitized = { ...payload };
  const emergencyContacts = sanitized['emergency_contacts'];
  if (Array.isArray(emergencyContacts)) {
    sanitized['emergency_contacts'] = emergencyContacts.map((contact) => {
      const row = { ...(contact as Record<string, unknown>) };
      const addresses = row['addresses'];
      if (Array.isArray(addresses)) {
        const cleaned = addresses.filter((address) => !isEmptyAddress(address));
        if (cleaned.length > 0) {
          row['addresses'] = cleaned;
        } else {
          delete row['addresses'];
        }
      }
      return row;
    });
  }
  return sanitized;
};
